import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/shared/services/apiClient';
import { API_CONFIG } from '@/shared/config/api.config';
import { ROUTES } from '@/shared/config/routes.config';
import { 
  User, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse 
} from '@/shared/types/api.types';
import { useAuthStore } from '@/features/auth/store/authStore';
import toast from 'react-hot-toast';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, token, setAuth, clearAuth } = useAuthStore();

  // Get current user profile
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const response = await api.get<{ user: User }>(API_CONFIG.ENDPOINTS.AUTH.PROFILE);
      return response.data?.user;
    },
    enabled: !!token,
    retry: false,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await api.post<AuthResponse>(
        API_CONFIG.ENDPOINTS.AUTH.LOGIN,
        credentials
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data && data.user && data.token) {
        setAuth(data.user, data.token);
        api.setAuth(data.token);
        toast.success('Welcome back!');
        navigate(ROUTES.DASHBOARD);
      } else {
        console.error('Invalid login response structure:', data);
        toast.error('Login response invalid. Please try again.');
      }
    },
    onError: (error: any) => {
      console.error('Login error:', error);
      const errorMessage = error?.message || error?.error?.message || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterRequest) => {
      const response = await api.post<{ 
        data: { userId: string; email: string; status: string } 
      }>(
        API_CONFIG.ENDPOINTS.AUTH.REGISTER,
        userData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Registration successful! Please check your email for verification.');
      navigate(ROUTES.AUTH.LOGIN);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Registration failed. Please try again.');
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    },
    onSuccess: () => {
      clearAuth();
      api.clearAuth();
      queryClient.clear();
      toast.success('Logged out successfully');
      navigate(ROUTES.AUTH.LOGIN);
    },
    onError: () => {
      // Even if the API call fails, clear local auth
      clearAuth();
      api.clearAuth();
      queryClient.clear();
      navigate(ROUTES.AUTH.LOGIN);
    },
  });

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD, { email });
    },
    onSuccess: () => {
      toast.success('Password reset email sent! Please check your inbox.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send reset email.');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
    },
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to change password.');
    },
  });

  // Email verification mutation
  const verifyEmailMutation = useMutation({
    mutationFn: async (token: string) => {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.VERIFY_EMAIL, { token });
    },
    onSuccess: () => {
      toast.success('Email verified successfully!');
      navigate(ROUTES.AUTH.LOGIN);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Email verification failed.');
    },
  });

  // MFA setup mutation
  const setupMFAMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post<{ 
        data: { backupCodes: string[] } 
      }>(API_CONFIG.ENDPOINTS.AUTH.MFA.SETUP, { code });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Two-factor authentication enabled successfully!');
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
      return data;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to enable two-factor authentication.');
    },
  });

  // MFA disable mutation
  const disableMFAMutation = useMutation({
    mutationFn: async (code: string) => {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.MFA.DISABLE, { code });
    },
    onSuccess: () => {
      toast.success('Two-factor authentication disabled successfully!');
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to disable two-factor authentication.');
    },
  });

  const login = (credentials: LoginRequest) => {
    loginMutation.mutate(credentials);
  };

  const register = (userData: RegisterRequest) => {
    registerMutation.mutate(userData);
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  const resetPassword = (email: string) => {
    resetPasswordMutation.mutate(email);
  };

  const changePassword = (data: { currentPassword: string; newPassword: string }) => {
    changePasswordMutation.mutate(data);
  };

  const verifyEmail = (token: string) => {
    verifyEmailMutation.mutate(token);
  };

  const setupMFA = (code: string) => {
    return setupMFAMutation.mutateAsync(code);
  };

  const disableMFA = (code: string) => {
    disableMFAMutation.mutate(code);
  };

  return {
    // State
    user: user || profile,
    token,
    isAuthenticated: !!token,
    isLoading: isLoadingProfile || 
      loginMutation.isPending || 
      registerMutation.isPending || 
      logoutMutation.isPending,

    // Actions
    login,
    register,
    logout,
    resetPassword,
    changePassword,
    verifyEmail,
    setupMFA,
    disableMFA,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isVerifyingEmail: verifyEmailMutation.isPending,
    isSettingUpMFA: setupMFAMutation.isPending,
    isDisablingMFA: disableMFAMutation.isPending,

    // Errors
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    resetPasswordError: resetPasswordMutation.error,
    changePasswordError: changePasswordMutation.error,
    verifyEmailError: verifyEmailMutation.error,
    setupMFAError: setupMFAMutation.error,
    disableMFAError: disableMFAMutation.error,
  };
};